/**
 * cdn-domain-access-control-tab.tsx — Access Control tab for a CDN domain (act-as).
 *
 * IP/CIDR/country/ASN allowlist and blocklist. Mode toggle selects which list is active.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/access-control
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Lock } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Switch, Skeleton, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNAccessControl,
  actAsCDNSecurityKeys,
  type AccessControlConfig,
  type AccessControlList,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';

type ListKey = 'ips' | 'cidrs' | 'countries' | 'asns';

const LIST_KEYS: { key: ListKey; label: string; placeholder: string }[] = [
  { key: 'ips',       label: 'IP Addresses',  placeholder: '1.2.3.4' },
  { key: 'cidrs',     label: 'CIDR Ranges',   placeholder: '192.168.0.0/24' },
  { key: 'countries', label: 'Countries',     placeholder: 'VN, US' },
  { key: 'asns',      label: 'ASNs',          placeholder: 'AS12345' },
];

interface FormList extends AccessControlList {
  _inputs: Partial<Record<ListKey, string>>;
}

interface AcForm {
  enabled: boolean;
  mode: 'allowlist' | 'blocklist';
  allowlist: FormList;
  blocklist: FormList;
}

function toFormList(l?: AccessControlList): FormList {
  return { ips: l?.ips ?? [], cidrs: l?.cidrs ?? [], countries: l?.countries ?? [], asns: l?.asns ?? [], _inputs: {} };
}

const DEFAULTS: AcForm = {
  enabled: false, mode: 'blocklist',
  allowlist: toFormList(), blocklist: toFormList(),
};

function hydrate(cfg: AccessControlConfig): AcForm {
  return {
    enabled: cfg.enabled ?? false,
    mode: cfg.mode ?? 'blocklist',
    allowlist: toFormList(cfg.allowlist),
    blocklist: toFormList(cfg.blocklist),
  };
}

function toPayload(f: AcForm): AccessControlConfig {
  const strip = (l: FormList): AccessControlList => ({ ips: l.ips, cidrs: l.cidrs, countries: l.countries, asns: l.asns });
  return { enabled: f.enabled, mode: f.mode, allowlist: strip(f.allowlist), blocklist: strip(f.blocklist) };
}

interface Props { domainId: string }

export function CdnDomainAccessControlTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNAccessControl(domainId);
  const [form, setForm] = useState<AcForm>(DEFAULTS);
  const [snapshot, setSnapshot] = useState<AcForm>(DEFAULTS);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data ? hydrate(q.data) : DEFAULTS;
    setForm(next); setSnapshot(next);
  }, [q.data]);

  const dirty = JSON.stringify(toPayload(form)) !== JSON.stringify(toPayload(snapshot));
  const setTop = <K extends keyof AcForm>(k: K, v: AcForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const activeList: 'allowlist' | 'blocklist' = form.mode;

  const addEntry = (lk: ListKey) => {
    const val = (form[activeList]._inputs[lk] ?? '').trim();
    if (!val) return;
    const entries = val.split(',').map((s) => s.trim()).filter(Boolean);
    setForm((f) => {
      const list = { ...f[activeList] };
      list[lk] = [...(list[lk] ?? []), ...entries.filter((e) => !(list[lk] ?? []).includes(e))];
      list._inputs = { ...list._inputs, [lk]: '' };
      return { ...f, [activeList]: list };
    });
  };

  const removeEntry = (lk: ListKey, idx: number) => {
    setForm((f) => {
      const list = { ...f[activeList] };
      list[lk] = (list[lk] ?? []).filter((_, i) => i !== idx);
      return { ...f, [activeList]: list };
    });
  };

  const setInput = (lk: ListKey, v: string) => {
    setForm((f) => {
      const list = { ...f[activeList], _inputs: { ...f[activeList]._inputs, [lk]: v } };
      return { ...f, [activeList]: list };
    });
  };

  if (q.isLoading) return <Skeleton style={{ height: 280 }} />;
  if (q.isError) {
    return <TabError title={t('cdn_domain.access_control_tab.load_error', 'Failed to load access control')} onRetry={() => q.refetch()} />;
  }

  return (
    <Section
      title={t('cdn_domain.access_control_tab.title', 'Access Control')}
      description={t('cdn_domain.access_control_tab.description', 'IP, CIDR, country, and ASN-based allow/block lists.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.access_control_tab.section_settings', 'Settings')}</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title={t('cdn_domain.access_control_tab.field_enabled', 'Access control enabled')}
              control={<Switch checked={form.enabled} onCheckedChange={(v) => setTop('enabled', v)} />}
            />
            {form.enabled && (
              <SettingRow
                title={t('cdn_domain.access_control_tab.field_mode', 'Mode')}
                description={t('cdn_domain.access_control_tab.field_mode_desc', 'Allowlist only permits listed sources; blocklist denies them.')}
                control={
                  <Select value={form.mode} onValueChange={(v) => setTop('mode', v as AcForm['mode'])}>
                    <SelectTrigger style={{ width: 160 }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="allowlist">{t('cdn_domain.access_control_tab.mode_allowlist', 'Allowlist')}</SelectItem>
                      <SelectItem value="blocklist">{t('cdn_domain.access_control_tab.mode_blocklist', 'Blocklist')}</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
            )}
          </CardBody>
        </Card>

        {form.enabled && (
          <Card>
            <CardHeader>
              <CardTitle>
                {activeList === 'allowlist'
                  ? t('cdn_domain.access_control_tab.section_allowlist', 'Allowlist')
                  : t('cdn_domain.access_control_tab.section_blocklist', 'Blocklist')}
              </CardTitle>
              <CardDescription>
                {t('cdn_domain.access_control_tab.section_list_desc', 'Sources in the active list. Comma-separate multiple values when adding.')}
              </CardDescription>
            </CardHeader>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
              {LIST_KEYS.map(({ key: lk, label, placeholder }) => {
                const entries = form[activeList][lk] ?? [];
                return (
                  <div key={lk} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                    <span style={{ fontWeight: 500, fontSize: 'var(--vn-font-size-sm)' }}>{label}</span>
                    {entries.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
                        {entries.map((e, i) => (
                          <Badge key={i} tone="neutral" appearance="subtle">
                            {e}
                            <IconButton variant="ghost" size="sm" aria-label="Remove" style={{ marginLeft: 2 }} onClick={() => removeEntry(lk, i)}>
                              <X size={11} />
                            </IconButton>
                          </Badge>
                        ))}
                      </div>
                    )}
                    {entries.length === 0 && (
                      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
                        {t('cdn_domain.access_control_tab.empty_list', 'None')}
                      </span>
                    )}
                    <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                      <Input
                        value={form[activeList]._inputs[lk] ?? ''}
                        placeholder={placeholder}
                        style={{ flex: 1 }}
                        onChange={(e) => setInput(lk, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEntry(lk); } }}
                      />
                      <Button variant="secondary" size="sm" startIcon={<Plus size={13} />} onClick={() => addEntry(lk)}>
                        {t('cdn_domain.access_control_tab.add_entry', 'Add')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardBody>
          </Card>
        )}

        {!form.enabled && (
          <EmptyState
            icon={<Lock size={28} />}
            title={t('cdn_domain.access_control_tab.disabled_title', 'Access control is disabled')}
            description={t('cdn_domain.access_control_tab.disabled_desc', 'Enable access control above to configure IP and geo restrictions.')}
          />
        )}

        <ActionBar
          end={
            <>
              <Button variant="secondary" disabled={!dirty} onClick={() => setForm(snapshot)}>{t('common.cancel', 'Cancel')}</Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>{t('cdn_domain.access_control_tab.action_save', 'Save')}</Button>
            </>
          }
        />
      </div>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.access_control_tab.confirm_save_title', 'Save access control')}
        summary={t('cdn_domain.access_control_tab.save_summary', 'Update IP/geo/ASN access control lists for this domain.')}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.accessControl(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/access-control`, toPayload(form))}
        onSuccess={() => setSnapshot(form)}
      />
    </Section>
  );
}
